import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { Leaf, Heart, Users, History, Flame, Coffee } from 'lucide-react';

// Social media icons as simple SVGs
const InstagramIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
);

const FacebookIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const TripAdvisorIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.006 4.295c-2.67 0-5.338.784-7.645 2.353H0l1.963 2.135a5.997 5.997 0 0 0 4.04 10.43 5.976 5.976 0 0 0 4.075-1.6L12 19.705l1.922-2.09a5.972 5.972 0 0 0 4.072 1.598 5.997 5.997 0 0 0 4.04-10.43L24 6.648h-4.35a13.573 13.573 0 0 0-7.644-2.353zM12 6.255a11.5 11.5 0 0 1 5.942 1.642 5.963 5.963 0 0 0-1.946 1.235A5.953 5.953 0 0 0 12 7.873a5.95 5.95 0 0 0-3.996 1.259 5.963 5.963 0 0 0-1.946-1.235A11.5 11.5 0 0 1 12 6.255zm-6.003 2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zm12.006 0a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zM6 10.25a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5zm12 0a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5z"/>
    </svg>
);

export function AboutPage() {
    return (
        <div className="page-container" data-testid="about-page">
            <PageHeader title="About Burston" showBack />
            
            <div className="px-4 py-4">
                {/* Hero - Garden Centre Overview Image */}
                <div className="rounded-3xl overflow-hidden mb-6">
                    <img
                        src="https://customer-assets.emergentagent.com/job_friendly-garden-app/artifacts/ek3283t4_Garden-centre-overview.jpg"
                        alt="Burston Garden Centre aerial view"
                        className="w-full h-48 object-cover"
                    />
                </div>

                {/* Story */}
                <div className="card-base mb-6" data-testid="about-story">
                    <div className="flex items-center gap-3 mb-4">
                        <Leaf className="w-8 h-8 text-primary" />
                        <h2 className="text-xl font-bold text-foreground">About Us</h2>
                    </div>
                    <div className="space-y-4 text-muted-foreground leading-relaxed">
                        <p>
                            We pride ourselves on our desire to exceed customer expectations. By bringing together a unique 
                            and talented team we deliver the ultimate garden centre experience.
                        </p>
                        <p>
                            Our specialist buyers travel far and wide to discover trends, products and plants with one aim 
                            in mind — to engage and excite customers on their visit to Burston.
                        </p>
                    </div>
                </div>

                {/* History */}
                <div className="card-base mb-6" data-testid="about-history">
                    <div className="flex items-center gap-3 mb-4">
                        <History className="w-8 h-8 text-primary" />
                        <h2 className="text-xl font-bold text-foreground">Our History</h2>
                    </div>
                    <div className="space-y-4 text-muted-foreground leading-relaxed">
                        <p>
                            The site on which Burston Garden Centre is located has been owned by the <strong className="text-foreground">Pearson family since 1978</strong>. 
                            To this day the family maintain a high level of interest, participation and commitment to the Garden Centre.
                        </p>
                        <p>
                            Brothers <strong className="text-foreground">John and Richard Pearson</strong> founded Burston Nurseries (adjacent to the Garden Centre) and were 
                            instrumental in establishing Burston Garden Centre on site in 1978. The centre is a family owned independent centre.
                        </p>
                        <p>
                            Since 1978 the Garden Centre has gone from strength to strength and has grown considerably and successfully over the years.
                        </p>
                    </div>
                </div>

                {/* Rebuild Story */}
                <div className="card-base mb-6 bg-accent/5 border-accent/20" data-testid="about-rebuild">
                    <div className="flex items-center gap-3 mb-4">
                        <Flame className="w-8 h-8 text-accent" />
                        <h2 className="text-xl font-bold text-foreground">Rising from the Ashes</h2>
                    </div>
                    <div className="space-y-4 text-muted-foreground leading-relaxed">
                        <p>
                            The building that you see today was a <strong className="text-foreground">total re-build in 1997</strong> following a catastrophic fire.
                        </p>
                    </div>
                </div>

                {/* Restaurant */}
                <div className="card-base mb-6" data-testid="about-restaurant">
                    <div className="flex items-center gap-3 mb-4">
                        <Coffee className="w-8 h-8 text-primary" />
                        <h2 className="text-xl font-bold text-foreground">The Restaurant</h2>
                    </div>
                    <div className="space-y-4 text-muted-foreground leading-relaxed">
                        <p>
                            The restaurant has mirrored the success of the centre, soon outgrowing its original 'café' existence. 
                            It was <strong className="text-foreground">fully refurbished in 2012</strong> and continues to serve delicious homemade food to our visitors.
                        </p>
                    </div>
                </div>

                {/* Values */}
                <h2 className="text-lg font-bold text-foreground mb-4">What We Stand For</h2>
                
                <div className="space-y-4">
                    <div className="card-base">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Heart className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-bold text-foreground mb-1">Quality Plants</h3>
                                <p className="text-muted-foreground">
                                    We source the best quality plants, many grown locally, to help your garden thrive.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="card-base">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Users className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-bold text-foreground mb-1">Family Values</h3>
                                <p className="text-muted-foreground">
                                    Family owned and operated since 1978, maintaining the personal touch that makes Burston special.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="card-base">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Leaf className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-bold text-foreground mb-1">Customer Experience</h3>
                                <p className="text-muted-foreground">
                                    Dedicated to exceeding expectations with unique products, expert advice, and inspiring displays.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Social Media Links */}
                <div className="mt-8 card-base" data-testid="social-links">
                    <h3 className="font-bold text-foreground mb-4 text-center">Follow Us</h3>
                    <div className="flex justify-center gap-4">
                        <a 
                            href="https://www.facebook.com/BurstonGardenCentre" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-14 h-14 rounded-full bg-[#1877F2] text-white flex items-center justify-center transition-transform hover:scale-110"
                            aria-label="Facebook"
                            data-testid="facebook-link"
                        >
                            <FacebookIcon />
                        </a>
                        <a 
                            href="https://www.instagram.com/burstongardencentre/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-14 h-14 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white flex items-center justify-center transition-transform hover:scale-110"
                            aria-label="Instagram"
                            data-testid="instagram-link"
                        >
                            <InstagramIcon />
                        </a>
                        <a 
                            href="https://www.tripadvisor.co.uk/Attraction_Review-g186306-d8544748-Reviews-Burston_Garden_Centre-St_Albans_Hertfordshire_England.html" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-14 h-14 rounded-full bg-[#00AF87] text-white flex items-center justify-center transition-transform hover:scale-110"
                            aria-label="TripAdvisor"
                            data-testid="tripadvisor-link"
                        >
                            <TripAdvisorIcon />
                        </a>
                    </div>
                </div>

                {/* Tagline */}
                <div className="mt-8 text-center">
                    <img 
                        src="https://customer-assets.emergentagent.com/job_friendly-garden-app/artifacts/yj9c46ty_burston-garden-centre-logo-hires.webp"
                        alt="Burston Garden Centre"
                        className="h-16 mx-auto mb-4"
                    />
                    <p className="text-lg font-medium text-primary italic">Growing Inspiration</p>
                </div>
            </div>
        </div>
    );
}

export default AboutPage;
